'use client';

import {
  FeedbackStatus,
  Permission,
  type Feedback,
  type FeedbackSource,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  Skeleton,
  Textarea,
  cn,
  healthCriticalRowClass,
} from '@up2stay/up2promo-ui';
import { ChevronDown, ChevronUp, Inbox, Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  FeedbackSourceLabel,
  FeedbackStars,
  FeedbackStatusBadge,
} from '@/core/components/feedback/feedback-bits';
import { isIntentionalInboxRowActivate } from '@/core/components/feedback/feedback-inbox-row-activate';
import { FEEDBACK_LIST_SURFACE_CLASS } from '@/core/components/feedback/feedback-surface';
import {
  ListEmptyState,
  ListFilterSelect,
  ListPageBody,
  ListSearchField,
  ListToolbarSurface,
} from '@/core/components/layout';
import {
  useFeedbackEntries,
  usePatchFeedbackEntry,
} from '@/core/hooks/use-feedback';
import { useLocations } from '@/core/hooks/use-locations';
import { useLocale } from '@/core/hooks/use-locale';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

const ALL = '__all__';

function guestHeadline(
  entry: Feedback,
  anonymousLabel: string,
): { primary: string; secondary?: string } {
  const name = entry.guestName?.trim();
  const email = entry.guestEmail?.trim();
  if (name && email) return { primary: name, secondary: email };
  if (email) return { primary: email };
  if (name) return { primary: name };
  return { primary: anonymousLabel };
}

function entryMatchesSearch(
  entry: Feedback,
  query: string,
  locationLabel: string,
  anonymousLabel: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const guest = guestHeadline(entry, anonymousLabel);
  const haystack = [
    guest.primary,
    guest.secondary ?? '',
    entry.comment ?? '',
    locationLabel,
    ...entry.answers.flatMap((a) => [
      a.questionLabel ?? '',
      Array.isArray(a.value) ? a.value.join(' ') : String(a.value ?? ''),
    ]),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function formatAnswerValue(
  value: unknown,
  labels: { yes: string; no: string },
): string {
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? labels.yes : labels.no;
  return String(value);
}

export function FeedbackInboxPanel() {
  const dict = useDictionary().feedback;
  const locale = useLocale();
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { locations } = useLocations();
  const { navigateShell } = useNavigateShell();
  const { can } = usePermissions();
  const canInvite = can(Permission.FEEDBACK_INVITE);
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState<FeedbackStatus | typeof ALL>(ALL);
  const [locationId, setLocationId] = useState<string>(ALL);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const pointerDownEntryIdRef = useRef<string | null>(null);

  const query = useMemo(
    () => ({
      status: status === ALL ? undefined : status,
      locationId: locationId === ALL ? undefined : locationId,
      limit: 50,
    }),
    [locationId, status],
  );

  const {
    data: entries = [],
    isLoading,
    error,
    refetch,
  } = useFeedbackEntries(workspaceId, query);
  const patchEntry = usePatchFeedbackEntry(workspaceId);

  const locationName = (id: string) =>
    locations.find((l) => l.id === id)?.name ?? id;

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      const loc =
        locations.find((l) => l.id === entry.locationId)?.name ??
        entry.locationId;
      return entryMatchesSearch(
        entry,
        searchQuery,
        loc,
        dict.inbox.anonymousGuest,
      );
    });
  }, [dict.inbox.anonymousGuest, entries, locations, searchQuery]);

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(locale === 'de' ? 'de-DE' : 'en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [locale],
  );

  const onPatch = async (
    entryId: string,
    body: { status?: FeedbackStatus; note?: string },
    options?: { notify?: boolean },
  ) => {
    try {
      await patchEntry.mutateAsync({ entryId, body });
      if (options?.notify) {
        toast.success(dict.inbox.updated);
      }
      if (body.note != null) {
        setNoteDraft('');
      }
    } catch {
      toast.error(dict.inbox.updateFailed);
    }
  };

  return (
    <ListPageBody>
      <ListToolbarSurface>
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
          <ListSearchField
            className="min-w-0 w-full sm:flex-1"
            variant="toolbar"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={dict.inbox.searchPlaceholder}
            aria-label={dict.inbox.searchPlaceholder}
            onClear={() => setSearchQuery('')}
          />
          <div className="flex min-w-0 w-full flex-wrap items-center gap-2 sm:w-auto">
            <ListFilterSelect
              ariaLabel={dict.inbox.filters.status}
              value={status}
              onValueChange={(v) => {
                setExpandedId(null);
                setStatus(v as FeedbackStatus | typeof ALL);
              }}
              options={[
                { value: ALL, label: dict.inbox.filters.allStatuses },
                { value: FeedbackStatus.NEW, label: dict.status.new },
                { value: FeedbackStatus.SEEN, label: dict.status.seen },
                {
                  value: FeedbackStatus.RESOLVED,
                  label: dict.status.resolved,
                },
              ]}
            />
            <ListFilterSelect
              ariaLabel={dict.inbox.filters.location}
              value={locationId}
              onValueChange={(v) => {
                setExpandedId(null);
                setLocationId(v);
              }}
              options={[
                { value: ALL, label: dict.inbox.filters.allLocations },
                ...locations.map((loc) => ({
                  value: loc.id,
                  label: loc.name ?? loc.id,
                })),
              ]}
            />
          </div>
        </div>
      </ListToolbarSurface>

      {isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full rounded-[var(--r-card)]" />
          <Skeleton className="h-16 w-full rounded-[var(--r-card)]" />
        </div>
      )}

      {error && (
        <ListEmptyState
          title={dict.inbox.loadFailed}
          action={
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void refetch()}
            >
              {dict.retry}
            </Button>
          }
        />
      )}

      {!isLoading && !error && entries.length === 0 && (
        <ListEmptyState
          icon={<Inbox className="h-6 w-6 text-foreground/70" />}
          title={dict.inbox.empty.title}
          description={dict.inbox.empty.description}
          action={
            canInvite ? (
              <Button
                type="button"
                size="sm"
                onClick={() => navigateShell(shellPaths.feedbackCompose)}
              >
                <Plus className="h-4 w-4" />
                {dict.invites.create}
              </Button>
            ) : undefined
          }
        />
      )}

      {!isLoading &&
        !error &&
        entries.length > 0 &&
        filteredEntries.length === 0 && (
          <ListEmptyState
            icon={<Inbox className="h-6 w-6 text-foreground/70" />}
            title={dict.inbox.empty.noResultsTitle}
            description={dict.inbox.empty.noResultsDescription}
          />
        )}

      {!isLoading && !error && filteredEntries.length > 0 ? (
        <ul className={FEEDBACK_LIST_SURFACE_CLASS}>
          {filteredEntries.map((entry) => {
            const open = expandedId === entry.id;
            const lowStar =
              entry.stars <= 2 && entry.status !== FeedbackStatus.RESOLVED;
            const guest = guestHeadline(entry, dict.inbox.anonymousGuest);
            const yesNo = { yes: dict.inbox.yes, no: dict.inbox.no };
            const preview =
              entry.comment?.trim() ||
              (entry.answers[0]
                ? formatAnswerValue(entry.answers[0].value, yesNo)
                : dict.inbox.noComment);

            return (
              <li
                key={entry.id}
                className={lowStar ? healthCriticalRowClass : undefined}
              >
                <button
                  type="button"
                  className={cn(
                    'flex w-full min-h-11 cursor-pointer items-start gap-3 px-4 py-3.5 text-left transition-colors',
                    'hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none',
                  )}
                  onPointerDown={() => {
                    pointerDownEntryIdRef.current = entry.id;
                  }}
                  onClick={(event) => {
                    const intentional = isIntentionalInboxRowActivate(
                      event,
                      pointerDownEntryIdRef.current,
                      entry.id,
                    );
                    pointerDownEntryIdRef.current = null;
                    if (!intentional) return;
                    setExpandedId(open ? null : entry.id);
                    if (!open && entry.status === FeedbackStatus.NEW) {
                      void onPatch(
                        entry.id,
                        { status: FeedbackStatus.SEEN },
                        { notify: false },
                      );
                    }
                  }}
                >
                  <FeedbackStars stars={entry.stars} />
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-semibold tracking-tight text-foreground">
                        {guest.primary}
                      </span>
                      <FeedbackStatusBadge status={entry.status} />
                    </div>
                    {guest.secondary ? (
                      <p className="truncate text-xs text-foreground/80">
                        {guest.secondary}
                      </p>
                    ) : null}
                    <p className="line-clamp-1 text-sm text-foreground/90">
                      {preview}
                    </p>
                    <p className="truncate text-xs text-foreground/80">
                      <span className="font-medium text-foreground">
                        {locationName(entry.locationId)}
                      </span>
                      <span className="text-foreground/50"> · </span>
                      <FeedbackSourceLabel
                        source={entry.source as FeedbackSource}
                      />
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 pt-0.5">
                    <span className="text-xs text-foreground/80 tabular-nums">
                      {dateFmt.format(new Date(entry.createdAt))}
                    </span>
                    {open ? (
                      <ChevronUp className="h-4 w-4 text-foreground/70" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-foreground/70" />
                    )}
                  </div>
                </button>

                {open ? (
                  <div className="flex flex-col gap-3 border-t border-border/50 px-4 pb-4 pt-3">
                    {entry.comment?.trim() ? (
                      <p className="text-pretty text-sm text-foreground">
                        {entry.comment}
                      </p>
                    ) : null}
                    {entry.answers.length > 0 ? (
                      <dl className="space-y-2.5 text-sm">
                        {entry.answers.map((a) => (
                          <div key={a.questionId}>
                            <dt className="text-xs font-medium text-foreground/80">
                              {a.questionLabel?.trim() ||
                                dict.inbox.unknownQuestion}
                            </dt>
                            <dd className="mt-0.5 font-medium text-foreground">
                              {formatAnswerValue(a.value, yesNo)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}
                    {entry.notes.length > 0 ? (
                      <div className="space-y-1 text-sm">
                        <p className="font-medium">{dict.inbox.notes}</p>
                        {entry.notes.map((n, i) => (
                          <p
                            key={`${n.createdAt}-${i}`}
                            className="text-foreground/80"
                          >
                            {n.body}
                          </p>
                        ))}
                      </div>
                    ) : null}
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                      <Textarea
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        placeholder={dict.inbox.notePlaceholder}
                        className="min-h-20 flex-1"
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={!noteDraft.trim() || patchEntry.isPending}
                          onClick={() =>
                            void onPatch(
                              entry.id,
                              { note: noteDraft.trim() },
                              { notify: true },
                            )
                          }
                        >
                          {dict.inbox.addNote}
                        </Button>
                        {entry.status !== FeedbackStatus.RESOLVED ? (
                          <Button
                            type="button"
                            size="sm"
                            disabled={patchEntry.isPending}
                            onClick={() =>
                              void onPatch(
                                entry.id,
                                { status: FeedbackStatus.RESOLVED },
                                { notify: true },
                              )
                            }
                          >
                            {dict.inbox.markResolved}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </ListPageBody>
  );
}
